import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const SITE_NAME = 'Conakry en Poche';
const SITE_URL = 'https://conakryenpoche.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

const METADATA_BY_LANGUAGE = {
  fr: {
    default: {
      title: `${SITE_NAME} | Guide local de Conakry`,
      description:
        'Découvrez les meilleures adresses, services, activités et événements de Conakry dans une application moderne et pratique.'
    },
    home: {
      title: `${SITE_NAME} | Votre guide complet de Conakry`,
      description:
        'Trouvez rapidement restaurants, hôtels, services, activités et actualités pour mieux explorer Conakry.'
    },
    categories: {
      title: `Toutes les catégories | ${SITE_NAME}`,
      description:
        'Parcourez toutes les catégories d\'entreprises disponibles à Conakry et trouvez rapidement le bon service.'
    },
    category: {
      title: `Catégorie | ${SITE_NAME}`,
      description:
        'Explorez les entreprises validées de cette catégorie et accédez aux meilleures adresses de Conakry.'
    },
    events: {
      title: `Événements à Conakry | ${SITE_NAME}`,
      description:
        'Découvrez les événements, sorties et rendez-vous à venir dans la capitale guinéenne.'
    },
    activities: {
      title: `Activités à découvrir | ${SITE_NAME}`,
      description:
        'Retrouvez des activités, idées de sorties et expériences locales à vivre à Conakry.'
    },
    news: {
      title: `Actualités | ${SITE_NAME}`,
      description:
        'Suivez les dernières actualités utiles et les informations locales autour de Conakry.'
    },
    search: {
      title: `Recherche | ${SITE_NAME}`,
      description:
        'Recherchez une entreprise, un service ou une adresse à Conakry en quelques secondes.'
    },
    contact: {
      title: `Contact | ${SITE_NAME}`,
      description:
        'Contactez l\'équipe Conakry en Poche pour toute question, assistance ou partenariat.'
    },
    livingInGuinea: {
      title: `Vivre en Guinée | ${SITE_NAME}`,
      description:
        'Accédez à des informations pratiques pour vivre, s\'installer et mieux comprendre la Guinée.'
    },
    taxi: {
      title: `Allo Taxi | ${SITE_NAME}`,
      description:
        'Commandez un taxi ou trouvez des informations liées au transport urbain à Conakry.'
    },
    carRental: {
      title: `Location de voiture | ${SITE_NAME}`,
      description:
        'Louez une voiture auprès d\'agences partenaires à Conakry.'
    },
    registerBusiness: {
      title: `Inscrire mon entreprise | ${SITE_NAME}`,
      description:
        'Ajoutez votre entreprise sur Conakry en Poche et augmentez votre visibilité locale.'
    },
    login: {
      title: `Connexion | ${SITE_NAME}`,
      description:
        'Connectez-vous à votre compte pour gérer votre profil, vos services et votre activité.'
    },
    profile: {
      title: `Mon profil | ${SITE_NAME}`,
      description:
        'Gérez votre profil, vos préférences et votre activité sur Conakry en Poche.'
    }
  },
  en: {
    default: {
      title: `${SITE_NAME} | Local guide to Conakry`,
      description:
        'Discover the best places, services, activities, and events in Conakry through a modern local guide.'
    },
    home: {
      title: `${SITE_NAME} | Your complete guide to Conakry`,
      description:
        'Quickly find restaurants, hotels, services, activities, and local news to explore Conakry.'
    },
    categories: {
      title: `All categories | ${SITE_NAME}`,
      description:
        'Browse all available business categories in Conakry and quickly find the right service.'
    },
    category: {
      title: `Category | ${SITE_NAME}`,
      description:
        'Explore verified businesses in this category and access useful local addresses in Conakry.'
    },
    events: {
      title: `Events in Conakry | ${SITE_NAME}`,
      description:
        'Discover upcoming events, outings, and local happenings in the Guinean capital.'
    },
    activities: {
      title: `Things to do | ${SITE_NAME}`,
      description:
        'Find activities, local experiences, and ideas for what to do in Conakry.'
    },
    news: {
      title: `News | ${SITE_NAME}`,
      description:
        'Stay informed with the latest useful local news and updates around Conakry.'
    },
    search: {
      title: `Search | ${SITE_NAME}`,
      description:
        'Search for a business, service, or address in Conakry in just a few seconds.'
    },
    contact: {
      title: `Contact | ${SITE_NAME}`,
      description:
        'Contact the Conakry en Poche team for support, questions, or partnerships.'
    },
    livingInGuinea: {
      title: `Living in Guinea | ${SITE_NAME}`,
      description:
        'Access practical information for living, moving to, and better understanding Guinea.'
    },
    taxi: {
      title: `Taxi service | ${SITE_NAME}`,
      description:
        'Request a taxi or find useful information about getting around Conakry.'
    },
    carRental: {
      title: `Car rental | ${SITE_NAME}`,
      description:
        'Rent a car from partner agencies in Conakry.'
    },
    registerBusiness: {
      title: `Register my business | ${SITE_NAME}`,
      description:
        'Add your business to Conakry en Poche and improve your visibility locally.'
    },
    login: {
      title: `Login | ${SITE_NAME}`,
      description:
        'Sign in to manage your profile, services, and activity on Conakry en Poche.'
    },
    profile: {
      title: `My profile | ${SITE_NAME}`,
      description:
        'Manage your profile, preferences, and activity on Conakry en Poche.'
    }
  }
};

function ensureMeta(selector, attributes = {}) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  return element;
}

function ensureCanonicalLink(url) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);
}

function getPageKey(pathname) {
  if (pathname === '/') return 'home';
  if (pathname === '/categories' || pathname === '/all-categories') return 'categories';
  if (pathname.startsWith('/category/')) return 'category';
  if (pathname === '/events') return 'events';
  if (pathname === '/activities') return 'activities';
  if (pathname.startsWith('/news')) return 'news';
  if (pathname.startsWith('/search')) return 'search';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/vivre-en-guinee') return 'livingInGuinea';
  if (pathname === '/allo' || pathname === '/register-taxi' || pathname === '/terms-taxi') return 'taxi';
  if (pathname === '/location-voiture' || pathname === '/register-rental' || pathname === '/dashboard-rental') return 'carRental';
  if (pathname === '/register-business') return 'registerBusiness';
  if (pathname === '/login' || pathname === '/reset-password' || pathname === '/email-verification') return 'login';
  if (pathname === '/profile' || pathname === '/complete-profile') return 'profile';
  return 'default';
}

export default function RouteMetadata() {
  const location = useLocation();
  const { language } = useLanguage();

  const metadata = useMemo(() => {
    const localizedMetadata = METADATA_BY_LANGUAGE[language] || METADATA_BY_LANGUAGE.fr;
    return localizedMetadata[getPageKey(location.pathname)] || localizedMetadata.default;
  }, [language, location.pathname]);

  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${location.pathname}${location.search || ''}`;
    const locale = language === 'en' ? 'en_US' : 'fr_FR';

    document.title = metadata.title;
    document.documentElement.lang = language === 'en' ? 'en' : 'fr';

    ensureMeta('meta[name="description"]', {
      name: 'description',
      content: metadata.description
    });
    ensureMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: metadata.title
    });
    ensureMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: metadata.description
    });
    ensureMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonicalUrl
    });
    ensureMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: locale
    });
    ensureMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: DEFAULT_IMAGE
    });
    ensureMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: metadata.title
    });
    ensureMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: metadata.description
    });
    ensureMeta('meta[name="twitter:url"]', {
      name: 'twitter:url',
      content: canonicalUrl
    });
    ensureMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: DEFAULT_IMAGE
    });
    ensureCanonicalLink(canonicalUrl);
  }, [language, location.pathname, location.search, metadata.description, metadata.title]);

  return null;
}
